import { useEffect, useMemo } from 'react';
import { TicketSystemFieldsKey, customSWRConfig } from '../../model/types';
import { UserForPlaceholder } from '../../store/childStore/macroStore';
import { getMacroStore } from '../../utils/store/getStore';
import useConditionOptions from '../../utils/useConditionOptions';
import useGroups from '../adminPage/useGroups';

interface AssigneeCascaderOptionType {
    key?: string;
    value?: number | string;
    children?: AssigneeCascaderOptionType[];
    userinfo?: UserForPlaceholder;
    label?: string;
}

export default function useCascaderAssigneeList(
    isPublic = false,
    config: customSWRConfig = {}
): {
    options: AssigneeCascaderOptionType[];
    optionsWithEmptyOption: AssigneeCascaderOptionType[];
    optionsForCondition: AssigneeCascaderOptionType[];
    optionsForAction: AssigneeCascaderOptionType[];
} {
    const { data: groupList } = useGroups(config, isPublic);
    const { localizeOptions } = useConditionOptions({ triggers: true });
    const options = useMemo(() => {
        const cascaderAssigneeList = groupList?.map((group) => {
            return {
                value: group.id,
                label: group.name,
                children:
                    group.users?.map((user) => {
                        return {
                            key: user.email,
                            value: user.id,
                            children: [],
                            userinfo: user,
                            label: `${user.fullName} (${user.email})`
                        };
                    }) ?? []
            };
        });

        return localizeOptions(TicketSystemFieldsKey.assignee, cascaderAssigneeList as any);
    }, [groupList, localizeOptions]);

    const optionsWithEmptyOption = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [{ label: '-', value: ':notAssigned' } as any]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );

    const optionsForCondition = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [
                { label: '-', value: ':notAssigned' } as any,
                { label: '(currentUser)', value: ':currentUser' } as any,
                { label: '(requester)', value: ':requester' } as any
            ]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );
    const optionsForAction = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [
                { label: '-', value: ':notAssigned' } as any,
                { label: '(currentUser)', value: ':currentUser' } as any
            ]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );
    useEffect(() => {
        options && getMacroStore().setAssigneeOptions(options);
    }, [options]);

    return { options, optionsWithEmptyOption, optionsForCondition, optionsForAction };
}

export { type AssigneeCascaderOptionType };
